import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import { 
  BookOpen, 
  Upload, 
  Clock, 
  Save, 
  FileText,
  FileDown,
  Image as ImageIcon,
  CheckCircle2,
  Trash2,
  ExternalLink
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_PDF_SIZE = 50 * 1024 * 1024; // 50MB for Handbook PDF

export default function ParentsHandbookForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: 'Parent & Student Handbook',
    title: 'Essential Guide to School Life',
    subtitle: "Complete resource for policies, schedules, and everything families need to know to support your child's success",
    handbook: {
      label: 'Download Now',
      title: 'Parent & Student Handbook',
      description: 'Your complete guide to school policies, daily schedules, academic standards, and support services—all in one comprehensive downloadable resource.',
      image: '/assets/news/news4.jpg',
      download_url: '/assets/handbook.pdf',
      file_size: '2.4 MB',
      file_pages: '48 Pages',
      file_date: 'May 2026',
      features: [
        'School Policies & Guidelines',
        'Daily Schedules & Routines',
        'Academic Standards & Grading',
        'Student Wellbeing Resources'
      ]
    },
    inside_topics: [
      { title: 'School Policies', description: 'Attendance, code of conduct, academic integrity, and dress code' },
      { title: 'Schedules & Routines', description: 'Daily schedules, break times, assembly times, and special events' },
      { title: 'Academic Information', description: 'Grading system, assessment methods, report cards, and meetings' },
      { title: 'Student Wellbeing', description: 'Counseling services, health & safety, and support programs' },
      { title: 'Safety & Security', description: 'Emergency procedures, campus safety, and security measures' },
      { title: 'Contact Directory', description: 'Staff contacts, department numbers, and emergency procedures' }
    ]
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    subtitle: false,
    handbook: false,
    inside_topics: false
  });
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [mediaMap, setMediaMap] = useState({});

  const getAuthHeaders = () => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  // Upload progress states
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);

  // New feature input state
  const [newFeatureText, setNewFeatureText] = useState('');

  // Fetch mediaMap from server for instant ID resolution
  const fetchMediaMap = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const json = await res.json();
        if (json.ok && Array.isArray(json.data)) {
          const map = {};
          const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
          json.data.forEach(m => {
            const mId = m.id || m.media_id;
            let mUrl = m.url || m.file_path;
            if (mUrl && !mUrl.startsWith('http://') && !mUrl.startsWith('https://')) {
              mUrl = `${origin}${mUrl.startsWith('/') ? '' : '/'}${mUrl}`;
            }
            if (mId && mUrl) {
              map[mId] = mUrl;
              map[String(mId)] = mUrl;
              const num = Number(mId);
              if (!isNaN(num)) map[num] = mUrl;
            }
          });
          setMediaMap(map);
          try {
            sessionStorage.setItem('cms_media_map', JSON.stringify(map));
          } catch (e) {}
        }
      }
    } catch (e) {
      console.warn('[ParentsHandbookForm] Media map fetch error:', e);
    }
  }, [apiBase]);

  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;
    if (mediaMap[str]) return mediaMap[str];
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    const resolved = resolveMediaSrc(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return str;
  }, [mediaMap, apiBase]);

  useEffect(() => {
    fetchMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=parents`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => 
          s.section_type === 'parents-handbook' || 
          s.section_type === 'parent-student-handbook' || 
          s.section_type === 'handbook'
        );
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=parents`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => 
            s.section_type === 'parents-handbook' || 
            s.section_type === 'parent-student-handbook' || 
            s.section_type === 'handbook'
          );
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);

        let content = foundSection.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = foundSection.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }

        const en = content?.en || content || {};
        const loadedData = {
          kicker: en.kicker || 'Parent & Student Handbook',
          title: en.title || 'Essential Guide to School Life',
          subtitle: en.subtitle || en.description || formData.subtitle,
          handbook: {
            ...formData.handbook,
            ...(en.handbook || {})
          },
          inside_topics: Array.isArray(en.inside_topics) && en.inside_topics.length > 0 
            ? en.inside_topics 
            : formData.inside_topics
        };

        setFormData(loadedData);
        setOriginalData(loadedData);

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pData = await pendingRes.json();
            if (pData.success && pData.data?.pending_changes) {
              const pFields = { kicker: false, title: false, subtitle: false, handbook: false, inside_topics: false };

              pData.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  if (['kicker', 'title', 'subtitle', 'handbook', 'inside_topics'].includes(c.field_id)) {
                    pFields[c.field_id] = true;
                  }
                }
              });

              setPendingFields(pFields);
            }
          }
        } catch (pe) {
          console.warn('[ParentsHandbookForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[ParentsHandbookForm] Load error:', err);
      toast.error('Failed to load Handbook content');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleHandbookChange = (subField, value) => {
    setFormData(prev => ({
      ...prev,
      handbook: { ...prev.handbook, [subField]: value }
    }));
  };

  const handleTopicChange = (index, subField, value) => {
    setFormData(prev => {
      const next = [...prev.inside_topics];
      next[index] = { ...next[index], [subField]: value };
      return { ...prev, inside_topics: next };
    });
  };

  // Add handbook key feature
  const handleAddFeature = () => {
    if (!newFeatureText.trim()) return;
    setFormData(prev => ({
      ...prev,
      handbook: {
        ...prev.handbook,
        features: [...(prev.handbook.features || []), newFeatureText.trim()]
      }
    }));
    setNewFeatureText('');
    toast.success('Poin fitur handbook ditambahkan');
  };

  const handleRemoveFeature = (fIdx) => {
    setFormData(prev => ({
      ...prev,
      handbook: {
        ...prev.handbook,
        features: (prev.handbook.features || []).filter((_, idx) => idx !== fIdx)
      }
    }));
  };

  // Upload Cover Image
  const handleCoverUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran foto maksimal 5MB');
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Format foto harus JPG, PNG, atau WebP');
      return;
    }

    setImageUploading(true);
    setImageProgress(10);

    const uploadData = new FormData();
    uploadData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (err) {}
    }
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setImageProgress(pct);
      }
    };

    xhr.onload = () => {
      setImageUploading(false);
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          let mediaUrl = data.url || data.file_path || res.url;
          if (mediaUrl && !mediaUrl.startsWith('http://') && !mediaUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            mediaUrl = `${origin}${mediaUrl.startsWith('/') ? '' : '/'}${mediaUrl}`;
          }
          if (mediaUrl) {
            handleHandbookChange('image', mediaUrl);
            toast.success('Foto sampul handbook berhasil diunggah');
          } else {
            toast.error('Gagal membaca URL media');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload cover');
        }
      } else {
        toast.error(`Upload cover gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(false);
      toast.error('Terjadi gangguan jaringan saat upload');
    };

    xhr.send(uploadData);
  };

  // Upload PDF Document
  const handlePdfUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_PDF_SIZE) {
      toast.error('Ukuran file PDF maksimal 50MB');
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Dokumen harus berformat PDF (.pdf)');
      return;
    }

    setPdfUploading(true);
    setPdfProgress(10);

    const uploadData = new FormData();
    uploadData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (err) {}
    }
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setPdfProgress(pct);
      }
    };

    xhr.onload = () => {
      setPdfUploading(false);
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          let docUrl = data.url || data.file_path || res.url;
          if (docUrl && !docUrl.startsWith('http://') && !docUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            docUrl = `${origin}${docUrl.startsWith('/') ? '' : '/'}${docUrl}`;
          }

          if (docUrl) {
            // Auto calculate file size in MB
            const sizeInMb = (file.size / (1024 * 1024)).toFixed(1);
            const formattedSize = sizeInMb > 0 ? `${sizeInMb} MB` : `${Math.round(file.size / 1024)} KB`;
            
            // Auto date format (e.g. October 2026)
            const currentDate = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

            setFormData(prev => ({
              ...prev,
              handbook: {
                ...prev.handbook,
                download_url: docUrl,
                file_size: formattedSize,
                file_date: currentDate
              }
            }));

            toast.success(`Dokumen PDF "${file.name}" (${formattedSize}) berhasil diunggah!`);
          } else {
            toast.error('Gagal membaca URL file PDF');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload PDF');
        }
      } else {
        toast.error(`Upload PDF gagal (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setPdfUploading(false);
      toast.error('Terjadi gangguan jaringan saat upload PDF');
    };

    xhr.send(uploadData);
  };

  // Submit field to approval workflow
  const handleSubmitField = async (field, customValue, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan');
      return;
    }

    const valToSubmit = customValue !== undefined ? customValue : formData[field];
    const oldVal = originalData[field];

    setSubmitting(prev => ({ ...prev, [field]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: field,
          field_name: fieldDisplayName || (field.charAt(0).toUpperCase() + field.slice(1)),
          old_value: oldVal,
          new_value: valToSubmit,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`"${fieldDisplayName || field}" diajukan untuk approval Kepala Sekolah`);
        setPendingFields(prev => ({ ...prev, [field]: true }));
        setOriginalData(prev => ({ ...prev, [field]: valToSubmit }));
        try {
          sessionStorage.removeItem('cms_page_parents');
          sessionStorage.removeItem('cms_sections_parents');
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ParentsHandbookForm] Submit error:', err);
      toast.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [field]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Handbook content...</p>
      </div>
    );
  }

  const previewCoverUrl = resolveImage(formData.handbook.image);

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Form Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <BookOpen size={22} />
        </div>
        <div>
          <h2 className="unified-title">Parent & Student Handbook</h2>
          <p className="unified-subtitle">
            Kelola dokumen panduan sekolah (PDF), foto sampul buku panduan, tanggal revisi, serta topik kebijakan penting bagi orang tua dan siswa.
          </p>
        </div>
      </div>

      {/* Section Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={pendingFields.kicker}
            placeholder="e.g. Parent & Student Handbook"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker', formData.kicker, 'Section Kicker')}
            disabled={pendingFields.kicker || submitting.kicker || formData.kicker === originalData.kicker}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Title</label>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={pendingFields.title}
            placeholder="e.g. Essential Guide to School Life"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', formData.title, 'Section Title')}
            disabled={pendingFields.title || submitting.title || formData.title === originalData.title}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Section Subtitle */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Subtitle</label>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <textarea
            className="unified-textarea"
            rows={2}
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={pendingFields.subtitle}
            placeholder="Complete resource for policies, schedules, and everything families need to know..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle', formData.subtitle, 'Section Subtitle')}
            disabled={pendingFields.subtitle || submitting.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} /> {submitting.subtitle ? 'Saving...' : (pendingFields.subtitle ? 'Pending' : 'Save')}
          </button>
        </div>
      </div>

      {/* Featured Handbook Card & PDF Upload */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileDown size={16} color="#2563eb" /> Handbook Download & Publication Card
            </label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Unggah file PDF resmi panduan sekolah agar pengguna dapat langsung mengunduh saat tombol "Download Handbook" diklik.
            </p>
          </div>
          {pendingFields.handbook && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>

        {/* PDF Uploader Box */}
        <div 
          style={{ 
            background: '#f8fafc', 
            border: '2px dashed #cbd5e1', 
            borderRadius: '14px', 
            padding: '20px', 
            marginBottom: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={24} />
              </div>
              <div>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', display: 'block' }}>
                  Dokumen PDF Handbook Sekolah
                </span>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Format: .pdf (Maksimal 50MB) • Otomatis terpasang ke tombol unduh
                </span>
              </div>
            </div>

            <label 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '8px',
                background: '#2563eb',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: pdfUploading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(37,99,235,0.2)'
              }}
            >
              <Upload size={15} />
              {pdfUploading ? `Mengunggah (${pdfProgress}%)...` : (formData.handbook.download_url ? 'Ganti File PDF' : 'Upload Dokumen PDF')}
              <input 
                type="file" 
                accept="application/pdf,.pdf" 
                style={{ display: 'none' }} 
                disabled={pendingFields.handbook || pdfUploading}
                onChange={handlePdfUpload}
              />
            </label>
          </div>

          {/* Current PDF file status bar */}
          {formData.handbook.download_url && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155' }}>
                <CheckCircle2 size={16} color="#10b981" />
                <span style={{ fontWeight: 600 }}>File PDF Aktif:</span>
                <span style={{ color: '#64748b', maxWidth: '340px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formData.handbook.download_url}
                </span>
              </div>
              <a
                href={formData.handbook.download_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12.5px', color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
              >
                Lihat / Test PDF <ExternalLink size={13} />
              </a>
            </div>
          )}
        </div>

        {/* Cover Photo and Metadata Details */}
        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '20px', alignItems: 'start', marginBottom: '20px' }}>
          {/* Cover Image */}
          <div>
            <div 
              style={{ 
                width: '180px', 
                height: '130px', 
                borderRadius: '12px', 
                overflow: 'hidden', 
                background: '#0f172a',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                border: '1px solid #cbd5e1',
                position: 'relative'
              }}
            >
              {previewCoverUrl ? (
                <img 
                  src={previewCoverUrl} 
                  alt="Handbook Preview" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                />
              ) : (
                <ImageIcon size={32} color="#94a3b8" />
              )}
              {imageUploading && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <div className="loading-spinner" style={{ width: '18px', height: '18px', marginBottom: '4px' }} />
                  <span style={{ fontSize: '11px', fontWeight: 600 }}>{imageProgress}%</span>
                </div>
              )}
            </div>
            <label 
              style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '6px', 
                marginTop: '8px', 
                width: '180px',
                padding: '6px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                fontSize: '12px', 
                color: '#2563eb', 
                cursor: pendingFields.handbook || imageUploading ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              <Upload size={13} />
              {imageUploading ? 'Uploading...' : 'Ganti Foto Sampul'}
              <input 
                type="file" 
                accept="image/jpeg,image/png,image/webp" 
                style={{ display: 'none' }} 
                disabled={pendingFields.handbook || imageUploading}
                onChange={handleCoverUpload}
              />
            </label>
          </div>

          {/* Handbook Text Fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label className="unified-label">Badge Label (Tombol Unduh)</label>
                <input
                  type="text"
                  className="unified-input"
                  value={formData.handbook.label || ''}
                  disabled={pendingFields.handbook}
                  onChange={(e) => handleHandbookChange('label', e.target.value)}
                  placeholder="e.g. Download Now"
                />
              </div>
              <div>
                <label className="unified-label">Judul Handbook</label>
                <input
                  type="text"
                  className="unified-input"
                  value={formData.handbook.title || ''}
                  disabled={pendingFields.handbook}
                  onChange={(e) => handleHandbookChange('title', e.target.value)}
                  placeholder="e.g. Parent & Student Handbook"
                />
              </div>
            </div>

            <div>
              <label className="unified-label">Deskripsi Handbook</label>
              <textarea
                className="unified-textarea"
                rows={2}
                value={formData.handbook.description || ''}
                disabled={pendingFields.handbook}
                onChange={(e) => handleHandbookChange('description', e.target.value)}
                placeholder="Penjelasan ringkas mengenai isi handbook..."
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div>
                <label className="unified-label">Ukuran File (File Size)</label>
                <input
                  type="text"
                  className="unified-input"
                  value={formData.handbook.file_size || ''}
                  disabled={pendingFields.handbook}
                  onChange={(e) => handleHandbookChange('file_size', e.target.value)}
                  placeholder="e.g. 2.4 MB"
                />
              </div>
              <div>
                <label className="unified-label">Jumlah Halaman (Pages)</label>
                <input
                  type="text"
                  className="unified-input"
                  value={formData.handbook.file_pages || ''}
                  disabled={pendingFields.handbook}
                  onChange={(e) => handleHandbookChange('file_pages', e.target.value)}
                  placeholder="e.g. 48 Pages"
                />
              </div>
              <div>
                <label className="unified-label">Bulan / Tahun Terbit</label>
                <input
                  type="text"
                  className="unified-input"
                  value={formData.handbook.file_date || ''}
                  disabled={pendingFields.handbook}
                  onChange={(e) => handleHandbookChange('file_date', e.target.value)}
                  placeholder="e.g. October 2026"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Key Features Checklist */}
        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
          <label className="unified-label" style={{ marginBottom: '8px', display: 'block' }}>
            Poin Sorotan Panduan (Key Features List)
          </label>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <input
              type="text"
              className="unified-input"
              value={newFeatureText}
              onChange={(e) => setNewFeatureText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddFeature();
                }
              }}
              placeholder="Tulis poin fitur baru (misal: 'School Policies & Guidelines')..."
            />
            <button
              type="button"
              className="unified-secondary-btn"
              onClick={handleAddFeature}
              style={{ whiteSpace: 'nowrap' }}
            >
              Tambah Fitur
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' }}>
            {(formData.handbook.features || []).map((feat, fIdx) => (
              <div key={fIdx} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1e293b' }}>
                  <CheckCircle2 size={15} color="#10b981" /> {feat}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveFeature(fIdx)}
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('handbook', formData.handbook, 'Handbook Publication Card')}
            disabled={pendingFields.handbook || submitting.handbook}
          >
            <Save size={14} /> {submitting.handbook ? 'Saving...' : (pendingFields.handbook ? 'Pending' : 'Save Handbook Card')}
          </button>
        </div>
      </div>

      {/* What's Inside 6 Topic Cards */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <div>
            <label className="unified-label">What's Inside (6 Topic Cards)</label>
            <p className="unified-field-desc" style={{ marginTop: '4px' }}>
              Topik-topik penting yang dirangkum di dalam buku panduan sekolah.
            </p>
          </div>
          {pendingFields.inside_topics && (
            <span className="unified-pending-pill">
              <Clock size={12} /> Pending Approval
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px', marginTop: '14px' }}>
          {formData.inside_topics.map((topic, tIdx) => (
            <div key={tIdx} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', background: '#f8fafc' }}>
              <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px', display: 'block' }}>
                Topik #{tIdx + 1}
              </label>
              <input
                type="text"
                className="unified-input"
                value={topic.title}
                disabled={pendingFields.inside_topics}
                onChange={(e) => handleTopicChange(tIdx, 'title', e.target.value)}
                style={{ marginBottom: '8px', fontWeight: 600 }}
              />
              <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px', display: 'block' }}>
                Penjelasan Topik
              </label>
              <textarea
                className="unified-textarea"
                rows={2}
                value={topic.description}
                disabled={pendingFields.inside_topics}
                onChange={(e) => handleTopicChange(tIdx, 'description', e.target.value)}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('inside_topics', formData.inside_topics, 'What\'s Inside Topics')}
            disabled={pendingFields.inside_topics || submitting.inside_topics}
          >
            <Save size={14} /> {submitting.inside_topics ? 'Saving...' : (pendingFields.inside_topics ? 'Pending' : 'Save Topics')}
          </button>
        </div>
      </div>
    </div>
  );
}
